import * as React from 'react';

import {
  $autoFocusRowId,
  CatalyticModalMode,
} from '@rematter/modules/trade/_state/loadStaged.state';
import { cn, ConsoleTouchDialog, useDebouncedValue } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import MiniSearch from 'minisearch';

import {
  CatalyticConverterDetailView,
  CatalyticItemTab,
} from './CatalyticConverterDetailView';
import { CatalyticConverterModalHeader } from './CatalyticConverterModalHeader';
import { CatalyticConverterRootView } from './CatalyticConverterRootView';

import {
  $catalyticItems,
  $catalyticRepresentativeItems,
  $groupedCatalyticItems,
} from '~/modules/trade/_state/loadForm.state';
import { InventoryItemRecord } from '~/modules/trade/types/load';
import {
  getRepresentativeItemId,
  resolveWholeVariantId,
} from '~/modules/trade/utils/catalyticConverter';
import { useTheme } from '~/shared/hooks';

const DEFAULT_TAB: CatalyticItemTab = 'vehicle';
const DEBOUNCE_DELAY_MS = 250;

type CatalyticConverterModalProps = {
  isOpen: boolean;
  /** Explicit modal mode defining user intent */
  mode: CatalyticModalMode;
  /** The inventory item ID to highlight (change mode) or show details (edit mode) */
  initialSelectedItemId?: string | null;
  /** The line item (row) ID to update when user picks a new item */
  lineItemId?: string | null;
  modalStyle?: React.CSSProperties;
  className?: string;
  touchModeEnabled?: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSelectItem: (itemId?: string) => void;
};

const createMiniSearch = () =>
  new MiniSearch({
    fields: ['itemGroup.name', 'name', 'mnemonic'],
    storeFields: ['id'],
    searchOptions: {
      prefix: true,
      boost: { mnemonic: 2 },
      fuzzy: 0.2,
    },
  });

export const CatalyticConverterModal = ({
  isOpen,
  mode,
  initialSelectedItemId,
  lineItemId,
  modalStyle,
  className,
  touchModeEnabled,
  onOpenChange,
  onSelectItem,
}: CatalyticConverterModalProps) => {
  const searchInputRef = React.useRef<HTMLDivElement>(null);

  const [searchTerm, setSearchTerm] = React.useState('');
  const [catalyticTab, setCatalyticTab] =
    React.useState<CatalyticItemTab>(DEFAULT_TAB);

  const catalyticItems = useAtomValue($catalyticItems);
  const representativeItems = useAtomValue($catalyticRepresentativeItems);
  const groupedItems = useAtomValue($groupedCatalyticItems);
  const setAutoFocusRowId = useSetAtom($autoFocusRowId);

  const { effectiveColorScheme } = useTheme();

  const isDetailView = mode === 'edit';

  const searchIndex = React.useMemo(() => {
    const miniSearch = createMiniSearch();
    miniSearch.addAll(representativeItems);
    return miniSearch;
  }, [representativeItems]);

  const representativeItemsById = React.useMemo(
    () => new Map(representativeItems.map((item) => [item.id, item])),
    [representativeItems],
  );

  const catalyticItemsById = React.useMemo(
    () => new Map(catalyticItems.map((item) => [item.id, item])),
    [catalyticItems],
  );

  const catalyticSearchResults = React.useMemo<InventoryItemRecord[]>(() => {
    if (!searchTerm) return [];

    return searchIndex
      .search(searchTerm)
      .map((result) => representativeItemsById.get(result.id))
      .filter((item): item is InventoryItemRecord => item !== undefined);
  }, [searchTerm, searchIndex, representativeItemsById]);

  const selectedCatalyticItem = React.useMemo(
    () =>
      initialSelectedItemId
        ? catalyticItemsById.get(initialSelectedItemId)
        : undefined,
    [initialSelectedItemId, catalyticItemsById],
  );

  const highlightedItemId = React.useMemo(() => {
    if (!selectedCatalyticItem) return undefined;
    return getRepresentativeItemId(groupedItems, selectedCatalyticItem);
  }, [selectedCatalyticItem, groupedItems]);

  const noResultsFound = useDebouncedValue(
    searchTerm.length > 0 && catalyticSearchResults.length === 0,
    DEBOUNCE_DELAY_MS,
  );

  const shouldAutoFocus = isOpen && !isDetailView;

  const handleSelectItem = React.useCallback(
    (itemId: string) => {
      const selectedItem = representativeItemsById.get(itemId);
      const resolvedItemId = selectedItem
        ? resolveWholeVariantId(groupedItems, selectedItem)
        : itemId;

      onSelectItem(resolvedItemId);
      onOpenChange(false);
      setAutoFocusRowId(null);
    },
    [
      representativeItemsById,
      groupedItems,
      onSelectItem,
      onOpenChange,
      setAutoFocusRowId,
    ],
  );

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      onOpenChange(open);
      setAutoFocusRowId(null);
    },
    [onOpenChange, setAutoFocusRowId],
  );

  const clearSearchTerm = React.useCallback(() => setSearchTerm(''), []);

  React.useEffect(() => {
    if (!isOpen) {
      setCatalyticTab(DEFAULT_TAB);
      setSearchTerm('');
    }
  }, [isOpen]);

  return (
    <ConsoleTouchDialog
      touchModeEnabled
      effectiveColorScheme={effectiveColorScheme}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      className="@CatalyticConverterModal"
      dialogClassName={cn('h-full w-full max-w-full', className)}
      classNameHeader="!pb-0"
      contentStyle={modalStyle}
      header={
        <CatalyticConverterModalHeader
          mode={mode}
          isDetailView={isDetailView}
          searchInputRef={searchInputRef}
          selectedItem={selectedCatalyticItem}
          activeTab={catalyticTab}
          onTabChange={setCatalyticTab}
          searchTerm={searchTerm}
          onChangeInput={setSearchTerm}
          clearSearchTerm={clearSearchTerm}
          autoFocus={shouldAutoFocus}
          touchModeEnabled={touchModeEnabled}
        />
      }
    >
      {isDetailView ? (
        <CatalyticConverterDetailView
          effectiveColorScheme={effectiveColorScheme}
          activeTab={catalyticTab}
          item={selectedCatalyticItem}
          lineItemId={lineItemId}
        />
      ) : (
        <CatalyticConverterRootView
          effectiveColorScheme={effectiveColorScheme}
          searchTerm={searchTerm}
          noResultsFound={noResultsFound}
          catalyticItems={representativeItems}
          catalyticSearchResults={catalyticSearchResults}
          selectedItemId={highlightedItemId}
          onSelectItem={handleSelectItem}
        />
      )}
    </ConsoleTouchDialog>
  );
};
